import React, { useEffect, useState } from 'react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { buildTaxonomyGovernanceReport, TaxonomyGovernanceReport } from './buildTaxonomyGovernanceReport';
import { warn } from '../../../utils/logger';
function copyJson(value: unknown) {
  const text = JSON.stringify(value, null, 2);
  navigator.clipboard.writeText(text).catch(() => warn('Failed to copy JSON payload'));
}
function Section({
  title,
  payload,
  children
}: {
  title: string;
  payload: unknown;
  children: React.ReactNode;
}) {
  return <Card className="p-4 border-slate-200 space-y-3" data-id="element-2118">
      <div className="flex items-center justify-between gap-3" data-id="element-2119">
        <h3 className="text-sm font-bold text-slate-900" data-id="element-2120">{title}</h3>
        <Button size="sm" variant="outline" onClick={() => copyJson(payload)} data-id="element-2121">
          Copy JSON
        </Button>
      </div>
      {children}
    </Card>;
}
export function TaxonomyGovernanceDiagnostics() {
  const [report, setReport] = useState<TaxonomyGovernanceReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    buildTaxonomyGovernanceReport().then(setReport).catch(err => {
      setError(err instanceof Error ? err.message : String(err));
    });
  }, []);
  if (error) {
    return <div className="p-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg" data-id="element-2122">
        <p className="font-bold mb-1" data-id="element-2123">Error loading governance report:</p>
        <p className="font-mono text-xs" data-id="element-2124">{error}</p>
      </div>;
  }
  if (!report) return <div className="p-4 text-sm text-slate-500" data-id="element-2125">
        Loading governance report…
      </div>;
  return <div className="space-y-6" data-id="element-2126">
      <Card className="p-4 border-slate-200" data-id="element-2127">
        <div className="flex flex-wrap items-center gap-2" data-id="element-2128">
          <Badge variant="outline" data-id="element-2129">
            Taxonomy families: {report.taxonomyTotalFamilies}
          </Badge>
          <Badge variant="outline" data-id="element-2130">
            Question families: {report.questionFamilies}
          </Badge>
          <Badge variant="outline" data-id="element-2131">
            Governance families: {report.governanceFamilies}
          </Badge>
          <Badge variant="outline" data-id="element-2132">
            Raw aids coverage: {report.questionsWithRawAidsPct}%
          </Badge>
          <Badge variant="outline" data-id="element-2133">
            Resolved aids coverage: {report.questionsWithResolvedAidsPct}%
          </Badge>
        </div>
      </Card>

      <Section title="Family mismatches" payload={{
      inTaxonomyNotInGovernance: report.inTaxonomyNotInGovernance,
      inGovernanceNotInTaxonomy: report.inGovernanceNotInTaxonomy,
      inTaxonomyNotInQuestions: report.inTaxonomyNotInQuestions,
      inQuestionsNotInTaxonomy: report.inQuestionsNotInTaxonomy
    }} data-id="element-2134">
        <div className="text-xs space-y-1 text-slate-700" data-id="element-2135">
          <p data-id="element-2136">
            Taxonomy not in governance:{' '}
            {report.inTaxonomyNotInGovernance.length}
          </p>
          <p data-id="element-2137">
            Governance not in taxonomy:{' '}
            {report.inGovernanceNotInTaxonomy.length}
          </p>
          <p data-id="element-2138">
            Taxonomy not in questions: {report.inTaxonomyNotInQuestions.length}
          </p>
          <p data-id="element-2139">
            Questions not in taxonomy: {report.inQuestionsNotInTaxonomy.length}
          </p>
        </div>
      </Section>

      <Section title="VisualAidFactory coverage" payload={{
      usedButMissing: report.aidTypesUsedButNotSupported,
      supportedButUnused: report.aidTypesSupportedButUnused,
      visualRegistryIntegrity: report.visualRegistryIntegrity
    }} data-id="element-2140">
        <div className="text-xs space-y-2" data-id="element-2141">
          <p data-id="element-2142">
            Used but unsupported types:{' '}
            {report.aidTypesUsedButNotSupported.length}
          </p>
          <p data-id="element-2143">
            Supported but unused types:{' '}
            {report.aidTypesSupportedButUnused.length}
          </p>
          <p data-id="element-2144">
            Registry integrity issues:{' '}
            {report.visualRegistryIntegrity.hasIntegrityIssues ? 1 : 0}
          </p>
        </div>
      </Section>

      <Section title="Resolved aid diagnostics" payload={{
      topZeroRawButNonZeroResolved: report.topZeroRawButNonZeroResolved,
      topRequiredButStillZeroResolved: report.topRequiredButStillZeroResolved
    }} data-id="element-2145">
        <div className="text-xs space-y-2" data-id="element-2146">
          <p data-id="element-2147">
            Top zero-raw but resolved:{' '}
            {report.topZeroRawButNonZeroResolved.length}
          </p>
          <p data-id="element-2148">
            Top required but still zero resolved:{' '}
            {report.topRequiredButStillZeroResolved.length}
          </p>
        </div>
      </Section>

      <Section title="Typo suggestions + legacy status" payload={{
      possibleTypos: report.possibleTypos,
      legacyStatus: report.legacyStatus,
      legacyStatusReasons: report.legacyStatusReasons
    }} data-id="element-2149">
        <div className="text-xs space-y-1 text-slate-700" data-id="element-2150">
          <p data-id="element-2151">Potential typos: {report.possibleTypos.length}</p>
          <p data-id="element-2152">Legacy status: {report.legacyStatus}</p>
          {report.legacyStatusReasons.map((reason, index) => <p key={index} data-id="element-2153">• {reason}</p>)}
        </div>
      </Section>
    </div>;
}